/* eslint-disable max-lines-per-function, max-statements */
import { useMutation, useQuery } from "@apollo/client";
import prepareComponent from "@bluframe/grapple/prepareComponent";
import useToggle from "@bluframe/grapple/useToggle";
import { mutations, queries } from "@bluframe/mightychroma-mightytool-graphql-schema";
import useUser from "hooks/useUser";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Item from "./Item";
const { content: { GET_CONTENT_AUTHORS }, news: { GET_NEWS } } = queries;
const { news: { GENERATE_DRAFT_NEWS, PUBLISH_DRAFT_NEWS, UPDATE_NEWS } } = mutations;
const { DELETE_NEWS } = mutations.news;
export const usePrepareComponent = () => {
    const [isEditing, toggleIsEditing] = useToggle();
    const params = useParams();
    const history = useHistory();
    const { isAdmin, isEditor } = useUser();
    const [cascadeHomeVideo, setCascadeHomeVideo] = useState(false);
    const { data: newsData, loading } = useQuery(GET_NEWS, {
        fetchPolicy: "network-only",
        variables: {
            id: params.id
        }
    });
    const { data: contentAuthorsData, loading: contentAuthorsLoading } = useQuery(GET_CONTENT_AUTHORS);
    const [updateNews, { loading: updateNewsLoading }] = useMutation(UPDATE_NEWS, {
        refetchQueries: [{ query: GET_NEWS, variables: { id: params.id } }]
    });
    const [generateDrafts, { loading: generateDraftsLoading }] = useMutation(GENERATE_DRAFT_NEWS, {
        refetchQueries: [{ query: GET_NEWS, variables: { id: params.id } }]
    });
    const [publishDrafts, { loading: publishDraftsLoading }] = useMutation(PUBLISH_DRAFT_NEWS, {
        refetchQueries: [{ query: GET_NEWS, variables: { id: params.id } }]
    });
    const [deleteNewsMutation, { loading: deleteNewsLoading }] = useMutation(DELETE_NEWS);
    const hasLinkedHomeVideo = Boolean(newsData?.getNews?.homeVideo?.id);
    useEffect(() => {
        if (!hasLinkedHomeVideo && cascadeHomeVideo) {
            const timeoutId = setTimeout(() => setCascadeHomeVideo(false), 0);
            return () => clearTimeout(timeoutId);
        }
        return undefined;
    }, [cascadeHomeVideo, hasLinkedHomeVideo]);
    const onCancel = () => {
        toggleIsEditing();
    };
    const onEdit = () => {
        toggleIsEditing();
    };
    const onGenerate = (additionalInstructions) => {
        generateDrafts({
            variables: {
                generateDraftsInputs: [
                    {
                        additionalInstructions,
                        id: params.id
                    }
                ]
            }
        });
    };
    const onPublishToggle = () => {
        if (newsData?.getNews?.isPublished) {
            updateNews({
                variables: { newsUpdateInput: { id: params.id, isPublished: false } }
            });
        }
        else {
            publishDrafts({
                variables: {
                    publishDraftsInputs: [
                        {
                            id: params.id
                        }
                    ]
                }
            });
        }
    };
    const onSave = (newsUpdateInput) => {
        updateNews({
            onCompleted: () => {
                if (isEditing) {
                    toggleIsEditing();
                }
            },
            variables: {
                newsUpdateInput: {
                    ...newsUpdateInput,
                    id: params.id
                }
            }
        });
    };
    const onDelete = () => {
        if (!params.id || deleteNewsLoading) {
            return;
        }
        deleteNewsMutation({
            onCompleted: () => {
                history.push("/drafts/news");
                setCascadeHomeVideo(false);
            },
            onError: () => {
                setCascadeHomeVideo(false);
            },
            variables: {
                cascadeHomeVideo,
                id: params.id
            }
        });
    };
    return {
        hasLinkedHomeVideo,
        isAdmin,
        contentAuthors: contentAuthorsData?.getContentAuthors,
        isEditing,
        isEditor,
        isContentAuthorsLoading: contentAuthorsLoading,
        isGenerateLoading: generateDraftsLoading,
        isPublishLoading: publishDraftsLoading,
        isUpdateLoading: updateNewsLoading,
        item: newsData?.getNews,
        loading,
        onCancel,
        onDelete,
        onDeleteModalClose: () => setCascadeHomeVideo(false),
        onEdit,
        onGenerate,
        onPublishToggle,
        onSave,
        onToggleCascadeDelete: () => setCascadeHomeVideo((prev) => !prev),
        shouldCascadeDelete: cascadeHomeVideo
    };
};
export default prepareComponent(usePrepareComponent)(Item);
